import * as React from 'react';
import {Dropdown, Form, InputGroup} from 'react-bootstrap';

interface Option {
  value: string | number;
  label: string;
  disabled?: boolean;
}

interface SearchableMultiSelectDropdownProps {
  name: string;
  disabled?: boolean;
  fullWidth?: boolean;
  label: string;
  values?: (string | number)[];
  variant?: string;
  onChange?: (
    event: React.ChangeEvent<HTMLSelectElement>,
    name: string,
    values: (string | number)[],
  ) => void;
  options: Option[];
  error?: string;
  touched?: boolean;
  required?: boolean;
  isSelectAll?: boolean;
}

const SearchableMultiSelectDropdown: React.FC<
  SearchableMultiSelectDropdownProps
> = ({
  name,
  disabled = false,
  fullWidth = false,
  label,
  values = [],
  variant,
  onChange,
  options = [],
  error,
  touched,
  required = false,
  isSelectAll = false,
}) => {
  const [selectedValues, setSelectedValues] = React.useState<
    (string | number)[]
  >(Array.isArray(values) ? values : []);
  const [searchQuery, setSearchQuery] = React.useState<string>('');
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const dropdownMenuRef = React.useRef<HTMLDivElement | null>(null);

  const handleSelect = (selected: string | number) => {
    let updatedValues: (string | number)[] = [];
    if (selectedValues.includes(selected)) {
      updatedValues = selectedValues.filter((item) => item !== selected);
    } else {
      updatedValues = [...selectedValues, selected];
    }
    setSelectedValues(updatedValues);
    if (onChange) {
      const event = {
        target: {name, value: updatedValues},
      } as unknown as React.ChangeEvent<HTMLSelectElement>;
      onChange(event, name, updatedValues);
    }
  };

  const renderSelectedValues = () => {
    if (!Array.isArray(selectedValues) || selectedValues.length === 0)
      return 'Select';
    return selectedValues
      .map(
        (selected) =>
          options.find((option) => option.value === selected)?.label,
      )
      .filter(Boolean)
      .join(', ');
  };

  const filteredData = options?.filter((item) =>
    item.label.toLowerCase()?.includes(searchQuery.toLowerCase()),
  );

  React.useEffect(() => {
    setSelectedValues(Array.isArray(values) ? values : []);
  }, [values]);

  React.useEffect(() => {
    const handleScroll = (event: Event) => {
      if (
        dropdownMenuRef.current &&
        dropdownMenuRef.current.contains(event.target as Node)
      ) {
        // Ignore scroll inside the dropdown
        return;
      }
      setIsDropdownOpen(false); // Close dropdown if scroll is outside
    };

    if (isDropdownOpen) {
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isDropdownOpen]);

 const handleSelectAll = () => {
   const selectableOptions = (searchQuery ? filteredData : options).filter(
     (opt) => !opt.disabled,
   );

   const selectableValues = selectableOptions.map((opt) => opt.value);

   let updatedValues: (string | number)[] = [];

   const isAllSelected = selectableValues.every((val) =>
     selectedValues.includes(val),
   );

   if (isAllSelected) {
     updatedValues = selectedValues.filter(
       (val) => !selectableValues.includes(val),
     );
   } else {
     updatedValues = Array.from(
       new Set([...selectedValues, ...selectableValues]),
     );
   }

   setSelectedValues(updatedValues);

   if (onChange) {
     const event = {
       target: { name, value: updatedValues },
     } as unknown as React.ChangeEvent<HTMLSelectElement>;
     onChange(event, name, updatedValues);
   }
 };

const selectableOptions = (searchQuery ? filteredData : options)?.filter(
  (opt) => !opt.disabled,
);

const isAllSelected =
  selectableOptions?.length > 0 &&
  selectableOptions.every((opt) => selectedValues.includes(opt.value));

  return (
    <Form.Group
      controlId="multiSelectDropdown"
      className="labelDropdownWrapper"
      style={{ width: fullWidth ? "100%" : "auto" }}
    >
      <Form.Label>
        {label} {!disabled && required && <span className="required">*</span>}
      </Form.Label>

      <Dropdown
        className={`dropdownStatic ${touched && error ? "is-invalid" : ""}`}
        autoClose="outside"
        show={isDropdownOpen}
        onToggle={(isOpen) => {
          setIsDropdownOpen(isOpen);
          if (!isOpen) setSearchQuery("");
        }}
      >
        <Dropdown.Toggle
          variant={variant || "outline-secondary"}
          disabled={disabled}
          className={`w-100 text-start ${touched && error ? "is-invalid" : ""}`}
        >
          {renderSelectedValues()}
        </Dropdown.Toggle>

        <Dropdown.Menu id={`dropdown-menu-${name}`} ref={dropdownMenuRef}>
          {!isSelectAll ? (
            <InputGroup className="p-2">
              <Form.Control
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchQuery(e.target.value)
                }
              />
              <span className="material-icons material-symbols-outlined multiselect_iconSearch">
                search
              </span>
              <span
                className="material-icons material-symbols-outlined multiselect_iconclose"
                onClick={() => setSearchQuery("")}
                style={{ cursor: "pointer" }}
              >
                close
              </span>
            </InputGroup>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginRight: "8px",
              }}
            >
              <div className="d-flex align-items-center ms-1 ">
                <Form.Check
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  disabled={filteredData?.length === 0}
                />
              </div>
              <InputGroup className="p-2" style={{ flex: 1 }}>
                <Form.Control
                  type="text"
                  placeholder="Search"
                  style={{ paddingLeft: "15px" }}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <span
                  className="material-icons material-symbols-outlined multiselect_iconclose"
                  onClick={() => setSearchQuery("")}
                  style={{ cursor: "pointer" }}
                >
                  close
                </span>
              </InputGroup>
            </div>
          )}
          {filteredData?.length > 0 ? (
            filteredData.map((item) => (
              <Dropdown.Item
                key={item.value}
                as="div"
                className={`d-flex align-items-center px-2 py-2 ${
                  item.disabled ? "disabled" : ""
                }`}
                onClick={() => !item.disabled && handleSelect(item.value)}
                style={{
                  cursor: item.disabled ? "not-allowed" : "pointer",
                  opacity: item.disabled ? 0.5 : 1,
                }}
              >
                <Form.Check
                  type="checkbox"
                  className="me-1"
                  checked={selectedValues.includes(item.value)}
                  onChange={() => !item.disabled && handleSelect(item.value)}
                  disabled={item.disabled || disabled}
                />
                <span className="ellipses_label" title={String(item.label)}>
                  {item.label}
                </span>
              </Dropdown.Item>
            ))
          ) : (
            <Dropdown.Item disabled>No Data Found</Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </Form.Group>
  );
};

export default SearchableMultiSelectDropdown;
